import { useState } from 'react';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { THRESHOLD_UNITS, Timezones } from 'infrastructure/enums';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';

import { generateGlucoseMessage } from '../blurbs/generate-glucose-blurb';
import {
  hyperglycemiaSymptoms,
  hypoglycemiaSymptoms,
} from '../templates/glucose';
import { getNursingInterventionValues } from '../helpers';

import type { IUseBloodGlucoseProps } from '../types';
import type { IBloodGlucoseValues } from 'components/molecules/rpm-alert-assessment/types';

export const useBloodGlucoseAssessment = ({
  type,
  activityId,
  activityDate,
  values,
  patientGuid,
  addComment,
  closeModal,
  handleSubmit,
}: IUseBloodGlucoseProps) => {
  const [openEmergencyModal, setOpenEmergencyModal] = useState<boolean>(false);
  const { patientInfo } = usePatientInfo({
    patientGuid,
  });

  const initialValues: IBloodGlucoseValues = {
    // Q1
    patient: undefined,
    other: null,
    relationship: null,
    // Q2
    AOC: null,
    // Q3
    last: null,
    next: null,
    // Q4
    isOneMoreReading: null,
    repeatReadingWas: null,
    bounds: null,
    // Q5
    symptoms: null,
    confirmedSymptoms: [],
    deniedSymptoms: [],
    symptomsOnset: null,
    emergencyCare: null,
    checkSymptoms: true,
    // Q6
    isTakeMedications: null,
    prescribed: [],
    // Q7
    bloodSugar: null,
    // Q8
    confirmedFactors: [],
    deniedFactors: [],
    // Q9
    details: null,
    // Q10
    nursingIntervention: [],
    notEscalated: [],
    interventionDetails: null,
    escalationToClinic: [],
    // Q11
    verbalized: null,
  };

  const reading = `${values.glucose} ${THRESHOLD_UNITS.Glucose}`;
  const timeZone =
    patientInfo?.loginInfo?.timezone || Timezones['America/Chicago'];

  const dataInPatientTimeZone = convertDateToTimeZone(
    new Date(Number(activityDate)),
    Timezones[timeZone],
  );
  const dateOfReading = DateFormatter({
    date: dataInPatientTimeZone,
  });

  const symptomsList =
    type === 'min' ? hypoglycemiaSymptoms : hyperglycemiaSymptoms;

  const emergentSymptoms = symptomsList
    .filter((item) => item.required)
    .map((item) => item.label);

  const preparedData = (data: IBloodGlucoseValues) => {
    const medications = data.prescribed.filter(Boolean);
    const isSpeakWithPatient = data.patient === 'Patient';
    const lastDate = data.last
      ? DateFormatter({ date: new Date(data.last) })
      : null;
    const nextDate = data.next
      ? DateFormatter({ date: new Date(data.next) })
      : null;
    const confirmedEmergentSymptoms = data.confirmedSymptoms?.filter(
      (item: string) => emergentSymptoms.includes(item),
    );
    const confirmedSymptomsWithoutEmergent = data.confirmedSymptoms?.filter(
      (item: string) => !emergentSymptoms.includes(item),
    );
    const nursingInterventionValues = getNursingInterventionValues(
      data?.nursingIntervention,
    );

    return {
      // Did you speak to patient?
      Q1: {
        reading,
        dateOfReading,
        patient: data.patient,
        relationship: data.relationship,
        speakWithPatient: isSpeakWithPatient,
        speakWithOther: data.other,
      },
      // Mental Status Assessment
      Q2: {
        AOC: data.AOC,
      },
      // Appointments
      Q3: {
        last: lastDate,
        next: nextDate,
      },
      //
      Q4: {
        isOneMoreReading: data.isOneMoreReading,
        bounds: data.bounds,
        repeatReadingWas: data.repeatReadingWas,
      },
      // Symptoms
      Q5: {
        confirmedSymptoms: confirmedSymptomsWithoutEmergent,
        deniedSymptoms: data.deniedSymptoms,
        isAllSymptomsDenied:
          data.deniedSymptoms?.length === symptomsList.length,
        confirmedEmergentSymptoms,
        symptomsOnset: data.symptomsOnset,
        emergencyModalAnswer: data.emergencyCare,
      },
      // Medications
      Q6: {
        isTakeMedications: data.isTakeMedications,
        medications,
        hasMedications: !!medications?.length,
      },
      Q7: {
        bloodSugar: data.bloodSugar,
      },
      // Factors Assessment
      Q8: {
        confirmedFactors: data.confirmedFactors,
        deniedFactors: data.deniedFactors,
      },
      Q9: {
        details: data.details,
      },
      // Nursing Intervention
      Q10: {
        ...nursingInterventionValues,
        notEscalated: data.notEscalated,
        interventionDetails: data.interventionDetails,
        escalationToClinic: data.escalationToClinic,
      },
      Q11: {
        verbalized: data.verbalized,
      },
    };
  };

  const toggleEmergencyModal = () => setOpenEmergencyModal((prev) => !prev);

  const onSaveForm = (formData: IBloodGlucoseValues) => {
    const preparedValues = preparedData(formData);
    const message = generateGlucoseMessage(preparedValues);

    handleSubmit(message);
    addComment(activityId);
    closeModal();
  };

  return {
    initialValues,
    openEmergencyModal,
    symptomsList,
    onSaveForm,
    toggleEmergencyModal,
  };
};
